/** Rich-text editor: Toolbar, WikiLinkMenu, RecoveryBanner, SaveState, uploads. */
export const editor = {
  'editor.saveState.saved': 'Saved',
  'editor.saveState.saving': 'Saving…',
  'editor.saveState.unsaved': 'Unsaved changes',
  'editor.saveState.local': 'Saved locally',
  'editor.saveState.conflict': 'Conflict',

  /* ── Toolbar / bubble menu (shared labels) ───────────────────────────────── */
  'editor.toolbar.undo': 'Undo',
  'editor.toolbar.redo': 'Redo',
  'editor.toolbar.heading1': 'Heading 1',
  'editor.toolbar.heading2': 'Heading 2',
  'editor.toolbar.heading3': 'Heading 3',
  'editor.toolbar.bold': 'Bold',
  'editor.toolbar.italic': 'Italic',
  'editor.toolbar.underline': 'Underline',
  'editor.toolbar.strikethrough': 'Strikethrough',
  'editor.toolbar.inlineCode': 'Inline code',
  'editor.toolbar.link': 'Link',
  'editor.toolbar.bulletList': 'Bullet list',
  'editor.toolbar.numberedList': 'Numbered list',
  'editor.toolbar.taskList': 'Task list',
  'editor.toolbar.quote': 'Quote',
  'editor.toolbar.codeBlock': 'Code block',
  'editor.toolbar.divider': 'Divider',
  'editor.toolbar.table': 'Table',
  'editor.toolbar.image': 'Image',

  /* ── Gather child pages ───────────────────────────────────────────────────── */
  'editor.gatherChildren.label': 'Gather sub-pages',
  'editor.gatherChildren.untitled': 'Untitled',
  'editor.gatherChildren.noChildren': 'This page has no sub-pages to gather.',
  'editor.gatherChildren.confirmTitle': 'Replace this page’s content?',
  'editor.gatherChildren.confirmBody':
    'The content of {count} sub-pages will replace whatever is on this page. You can undo this with Ctrl/⌘+Z.',
  'editor.gatherChildren.confirm': 'Replace',
  'editor.gatherChildren.done': 'Gathered {count} sub-pages.',
  'editor.gatherChildren.error': 'The sub-pages could not be gathered.',

  /* ── Highlight / text colour ──────────────────────────────────────────────── */
  'editor.color.highlight': 'Highlight',
  'editor.color.textColor': 'Text color',
  'editor.color.remove': 'Remove color',
  'editor.color.amber': 'Amber',
  'editor.color.green': 'Green',
  'editor.color.blue': 'Blue',
  'editor.color.purple': 'Purple',
  'editor.color.red': 'Red',
  'editor.color.gray': 'Gray',

  'editor.wordCount.one': '{count} word',
  'editor.wordCount.other': '{count} words',

  /* ── Placeholder text ─────────────────────────────────────────────────────── */
  'editor.placeholder.empty': "Type '/' for commands, or just start writing…",
  'editor.placeholder.heading': 'Heading',

  /* ── Find & replace ───────────────────────────────────────────────────────── */
  'editor.search.label': 'Find and replace',
  'editor.search.find': 'Find',
  'editor.search.findPlaceholder': 'Find in page',
  'editor.search.replace': 'Replace',
  'editor.search.replacePlaceholder': 'Replace with',
  'editor.search.replaceOne': 'Replace',
  'editor.search.replaceAll': 'Replace all',
  'editor.search.next': 'Next match',
  'editor.search.previous': 'Previous match',
  'editor.search.caseSensitive': 'Match case',
  'editor.search.showReplace': 'Show replace',
  'editor.search.hideReplace': 'Hide replace',
  'editor.search.close': 'Close',
  'editor.search.noResults': 'No results',
  'editor.search.matchCount': '{current}/{total}',

  /* ── Link prompt ──────────────────────────────────────────────────────────── */
  'editor.link.prompt': 'Link URL',

  /* ── Wiki links ───────────────────────────────────────────────────────────── */
  'editor.wikiLink.brokenTitle': "{text} — this page doesn't exist yet. Click to create it.",
  'editor.wikiLink.searching': 'Searching…',
  'editor.wikiLink.noMatch': 'No pages match “{query}”.',
  'editor.wikiLink.createPagePrefix': 'Create page “',
  'editor.wikiLink.createPageSuffix': '”',

  /* ── Recovery banner ──────────────────────────────────────────────────────── */
  'editor.recovery.unsavedChanges': 'You have unsaved local changes from {time} ({ago}).',
  'editor.recovery.explanation':
    'This page was also changed elsewhere since then. Nothing has been lost — choose which version to keep.',
  'editor.recovery.keepMine': 'Keep mine',
  'editor.recovery.useServerVersion': 'Use server version',
  'editor.recovery.showDiff': 'Show diff',
  'editor.recovery.diffTitle': 'Your version vs. the server’s',
  'editor.recovery.diffDescription':
    'Plain text, for comparison only. There is no automatic merge — pick a side.',
  'editor.recovery.yourVersion': 'your unsaved version',
  'editor.recovery.serverVersion': 'the server’s',

  /* ── Uploads ──────────────────────────────────────────────────────────────── */
  'editor.upload.tooLarge': '{name} is larger than 10 MB.',
  'editor.upload.unsupportedType': '{name} is not an image we can store.',
  'editor.upload.unreadable': '{name} could not be read as an image.',
  'editor.upload.failed': 'An image could not be uploaded.',
  'editor.upload.failedDescription':
    'It is saved on this device. Use Retry on the image to try again.',

  'editor.image.uploading': 'Uploading',
  'editor.image.notUploadedYet': 'Not uploaded yet — saved on this device.',
  'editor.image.uploadFile': 'Upload file…',
  'editor.image.fromUrl': 'From URL…',
  'editor.image.urlPlaceholder': 'https://example.com/image.png',
  'editor.image.add': 'Add',
  'editor.image.urlInvalid': 'Enter a valid http(s) image URL.',

  /* ── Paste toasts ─────────────────────────────────────────────────────────── */
  'editor.paste.droppedLocalImages.one': '{count} image could not be pasted.',
  'editor.paste.droppedLocalImages.other': '{count} images could not be pasted.',
  'editor.paste.droppedLocalImagesDescription':
    'It was a link to a file on your computer, and the clipboard did not carry the picture itself. Copy the image on its own and paste it again.',
  'editor.paste.remoteImages.one': '{count} image link to another website and may stop working.',
  'editor.paste.remoteImages.other': '{count} images link to another website and may stop working.',
  'editor.paste.remoteImagesDescription': 'Right-click → save, then paste, to keep a copy.',
} as const;
